import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import useApi from '../hook/useAPI';
import { httpClient } from '../utils/http-client';
import { TIME_LOGS } from '../utils/const';
import {
  TIME_LOG_ACTION,
  TIME_LOG_STATE,
  TIME_LOG_STATE_LABEL,
  DEFAULT_TIME_LOG_SOURCE,
} from '../constants/timeLog';
import {
  loadWorkClockShiftSelection,
  saveWorkClockShiftSelection,
} from '../utils/workClockShiftStorage';

const WorkClockContext = createContext(null);

const getGeolocation = () => new Promise((resolve) => {
  if (!navigator.geolocation) {
    resolve({});
    return;
  }

  navigator.geolocation.getCurrentPosition(
    (position) => {
      resolve({
        latitude: String(position.coords.latitude),
        longitude: String(position.coords.longitude),
      });
    },
    () => resolve({}),
    { enableHighAccuracy: true, timeout: 10000, maximumAge: 60000 }
  );
});

const ACTION_ENDPOINT = {
  [TIME_LOG_ACTION.IN]: TIME_LOGS.CLOCK_IN,
  [TIME_LOG_ACTION.PAUSE]: TIME_LOGS.CLOCK_PAUSE,
  [TIME_LOG_ACTION.RESUME]: TIME_LOGS.CLOCK_RESUME,
  [TIME_LOG_ACTION.OUT]: TIME_LOGS.CLOCK_OUT,
};

const initialSelection = loadWorkClockShiftSelection() || {};

export function WorkClockProvider({ children, enabled = true }) {
  const { getData } = useApi();
  const navigate = useNavigate();
  const location = useLocation();
  const [clockState, setClockState] = useState(TIME_LOG_STATE.OFF);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [logs, setLogs] = useState([]);
  const [actionLoading, setActionLoading] = useState(false);
  const [activeShift, setActiveShift] = useState(null);
  const [selectedDeviceId, setSelectedDeviceId] = useState(initialSelection.deviceId || '');
  const [selectedDeviceName, setSelectedDeviceName] = useState(initialSelection.deviceName || '');
  const [operatorName, setOperatorNameState] = useState(initialSelection.operatorName || '');

  const applyStatus = useCallback((response) => {
    if (!response) {
      return;
    }
    if (response.clockState) {
      setClockState(response.clockState);
    }
    if (response.elapsedSeconds !== undefined && response.elapsedSeconds !== null) {
      setElapsedSeconds(Number(response.elapsedSeconds) || 0);
    }
    if (response.activeShift !== undefined) {
      setActiveShift(response.activeShift);
      if (response.activeShift?.deviceId) {
        setSelectedDeviceId(response.activeShift.deviceId);
        setSelectedDeviceName(response.activeShift.deviceName || '');
        setOperatorNameState(response.activeShift.notes || '');
      }
    }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const response = await getData(TIME_LOGS.STATUS, null, false);
      if (response?.status === true || response?.clockState) {
        applyStatus(response);
      }
    } catch (err) {
      console.error('Failed to fetch work clock status', err);
    }
  }, [applyStatus, getData]);

  const fetchLogs = useCallback(async (logLimit = 20) => {
    try {
      const response = await getData(`${TIME_LOGS.LIST}?page=1&limit=${logLimit}`, null, false);
      if (response?.status === 200) {
        setLogs(response.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch work clock logs', err);
    }
  }, [getData]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    fetchStatus();
  }, [enabled, fetchStatus]);

  useEffect(() => {
    if (clockState !== TIME_LOG_STATE.ACTIVE) {
      return undefined;
    }

    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);

    return () => clearInterval(interval);
  }, [clockState]);

  const setSelectedDevice = useCallback((deviceId, deviceName = '') => {
    setSelectedDeviceId(deviceId);
    setSelectedDeviceName(deviceName);
    saveWorkClockShiftSelection({
      deviceId,
      deviceName,
      operatorName,
    });
  }, [operatorName]);

  const setOperatorName = useCallback((name) => {
    setOperatorNameState(name);
    if (selectedDeviceId) {
      saveWorkClockShiftSelection({
        deviceId: selectedDeviceId,
        deviceName: selectedDeviceName,
        operatorName: name,
      });
    }
  }, [selectedDeviceId, selectedDeviceName]);

  const postClockAction = useCallback(async (action, logLimit = 20) => {
    if (action === TIME_LOG_ACTION.IN && !selectedDeviceId) {
      toast.error('Please select a device on the Time Tracking page before clocking in.');
      if (location.pathname !== TIME_LOGS.PAGE) {
        navigate(TIME_LOGS.PAGE, { state: { highlightDeviceSelect: true } });
      }
      return;
    }

    setActionLoading(true);
    try {
      const coords = await getGeolocation();
      const token = localStorage.getItem('token');
      const body = {
        ...coords,
        source: DEFAULT_TIME_LOG_SOURCE,
      };

      if (action === TIME_LOG_ACTION.IN) {
        body.deviceId = selectedDeviceId;
        if (operatorName.trim()) {
          body.notes = operatorName.trim();
        }
      }

      const { data: response } = await httpClient.post(
        ACTION_ENDPOINT[action],
        body,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response?.status === 201) {
        if (response.warning) {
          toast.warn(response.warning);
        } else {
          toast.success(response.message);
        }
        if (response.clockState) {
          setClockState(response.clockState);
        }
        if (response.clockState === TIME_LOG_STATE.OFF) {
          setActiveShift(null);
        }
        await fetchStatus();
        await fetchLogs(logLimit);
      } else if (response?.message) {
        toast.error(response.message);
      }
    } catch (err) {
      const message = err?.response?.data?.message || 'Work clock action failed';
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [
    fetchLogs,
    fetchStatus,
    location.pathname,
    navigate,
    operatorName,
    selectedDeviceId,
  ]);

  const value = useMemo(() => ({
    clockState,
    clockStateLabel: TIME_LOG_STATE_LABEL[clockState] || clockState,
    clockedIn: clockState !== TIME_LOG_STATE.OFF,
    elapsedSeconds,
    logs,
    actionLoading,
    activeShift,
    selectedDeviceId,
    selectedDeviceName,
    operatorName,
    setSelectedDevice,
    setOperatorName,
    fetchStatus,
    fetchLogs,
    handleClockIn: (logLimit) => postClockAction(TIME_LOG_ACTION.IN, logLimit),
    handlePause: (logLimit) => postClockAction(TIME_LOG_ACTION.PAUSE, logLimit),
    handleResume: (logLimit) => postClockAction(TIME_LOG_ACTION.RESUME, logLimit),
    handleClockOut: (logLimit) => postClockAction(TIME_LOG_ACTION.OUT, logLimit),
  }), [
    actionLoading,
    activeShift,
    clockState,
    elapsedSeconds,
    fetchLogs,
    fetchStatus,
    logs,
    operatorName,
    postClockAction,
    selectedDeviceId,
    selectedDeviceName,
    setOperatorName,
    setSelectedDevice,
  ]);

  return (
    <WorkClockContext.Provider value={value}>
      {children}
    </WorkClockContext.Provider>
  );
}

export function useWorkClockContext() {
  const context = useContext(WorkClockContext);
  if (!context) {
    throw new Error('useWorkClockContext must be used within WorkClockProvider');
  }
  return context;
}
